/**
 * The go-live checklist's pure rules — four from U4, a fifth ({@link countryVerdictOf})
 * added for the per-country readiness read.
 *
 * A SEPARATE FILE FOR THE REASON `products-model.ts` IS ONE, and the reason is measured
 * rather than stylistic: react-refresh's rule fires on any module that exports both a
 * component and a value, and `GoLivePage.tsx` exporting the original four took the web lint
 * from 19 warnings to 23 on the first run. The ceiling is a ratchet, so the functions move out
 * rather than the number moving up — the same trade the merged products screen made.
 *
 * It also puts the claims where they can be argued about on their own: every function
 * here answers a question with one right answer, and `golive.test.tsx` asserts each in
 * isolation as well as through the screen.
 */

import { isMaskedAmount } from '../inquiries/format';
import { COUNTRY_CODE_BY_ID } from './catalogue-api';
import type { CountryProductDto, ProductDto } from './catalogue-api';
import type { CountryReadinessDto, HealthReportDto } from './infra-api';
import type { CountryCode } from '../../domain/countries';

export type Verdict = 'ready' | 'not-ready' | 'unknown';

/**
 * The one answer at the top, TAKEN from the server.
 *
 * `unknown` when `/health` could not be read or could not read the database: a checklist
 * that cannot reach the gate says so rather than guessing in either direction. Note the
 * second arm — `verify_before_prod === null` means the counts were not measured, and a
 * `go_live_ready` computed over nothing must not be rendered as an answer.
 *
 * ⚠ NEVER RECOMPUTED FROM THE ROWS. `/health.go_live_ready` already ANDs the name queue,
 * the search probe and — since owner Fork 12 — every country's ability to issue a document;
 * a second implementation here would drift the first time any side gains a condition, and
 * the drift would be silent in the direction that matters — a green screen over a closed
 * gate.
 *
 * ⚠ RULE-5 CALLOUT (India go-live, cluster A5) — THE WARNING BECAME A FACT. This paragraph
 * named two conditions ("the name queue with the search probe"), and the gate now has three.
 * The prediction it made — *"would drift the first time either side gains a condition"* —
 * is exactly what happened, and the reason this file was already right is that it never
 * recomputed. It also can no longer be recomputed here at all: the third term is per-country
 * and Fork 11 keeps every per-country thing off the unauthenticated `/health` body, so there
 * is nothing to AND with even for somebody who tried. Nothing in this function changes.
 */
export function verdictOf(health: HealthReportDto | undefined): Verdict {
  if (health === undefined) return 'unknown';
  if (health.verify_before_prod === null) return 'unknown';
  return health.go_live_ready ? 'ready' : 'not-ready';
}

/**
 * THE OTHER ANSWER: can the country in the URL issue a document today?
 *
 * A SECOND FUNCTION AND NOT A WIDER {@link reconcile} OR A WIDER {@link verdictOf} — this
 * file's header states the rule and this is the case it was written for. `verdictOf` answers
 * *is the GROUP ready to go live*, over a group-wide counter and a search probe.
 * `countryVerdictOf` answers *can THIS COUNTRY produce a quotation*, over the things that
 * actually refuse one. They are different questions with different right answers, and on
 * today's data they disagree about India: zero flagged Indian product names (so the counter
 * behind `verdictOf` sees nothing wrong with India) while `countries.address_block` is an
 * unconfirmed placeholder, so `POST /quotes/:id/pdf` refuses with
 * `COUNTRY_LETTERHEAD_UNUSABLE` and India cannot print a quotation at all. Folding the two
 * into one function would force one of the two answers to be discarded.
 *
 * ⚠ TAKEN FROM `can_issue_documents`, NEVER RECOMPUTED FROM `items`. This is the same rule
 * as the one written out at :31-34 for the same reason, and here it has teeth the other one
 * does not: `items` is a per-item report and `can_issue_documents` is the SERVER's verdict
 * over the GATING subset. A client that re-derived "ready" by walking the items would be
 * re-implementing the membership rule — which items gate and which are merely reported — in
 * a second place, and the first time the server adds a gating item this build has never
 * heard of, the screen would call a shut gate open. `golive.test.tsx` feeds this an
 * INCOHERENT payload (`can_issue_documents: false` while every item reads satisfied) and
 * requires NOT READY, which only a rule that reads the boolean can produce.
 *
 * `unknown` when the route could not be read: the country's own gate is then unmeasured, and
 * unmeasured is not "ready" and not "not ready". There is no second `unknown` arm the way
 * {@link verdictOf} has one for un-measured counts — this route either answers with a
 * verdict or fails, and a partial body is not a shape it can return.
 */
export function countryVerdictOf(readiness: CountryReadinessDto | undefined): Verdict {
  if (readiness === undefined) return 'unknown';
  return readiness.can_issue_documents ? 'ready' : 'not-ready';
}

/**
 * How the screen's own list compares with the counter it claims to explain.
 *
 * `'agree'`      — the list is the counter. The only state in which an empty list may be
 *                  rendered as an all-clear.
 * `'scoped'`     — the reader sees fewer countries than `/health` counts. Legitimate, and
 *                  it must be SAID: their empty list is not the group's answer.
 * `'disagree'`   — the numbers differ where they should not, or the catalogue read
 *                  ADMITS it was short (`complete: false`).
 * `'unmeasured'` — `/health` could not be read at all, so there is nothing to compare and
 *                  the list stands alone.
 *
 * ⚠ EQUAL NUMBERS ON A SCOPED READ REALLY IS 'agree', AND THE REASON IS SET THEORY RATHER
 * THAN OPTIMISM: a flagged product in the reader's countries is also counted by the
 * global counter, so the scoped list is a SUBSET of what `/health` counts. A subset whose
 * size equals the whole is the whole. `catalogue-pricing.e2e-spec.ts` (7c) measures the
 * subset relation itself, which is what makes this argument standable-on.
 *
 * `complete: false` outranks everything except an unreadable gate: a read that knows it
 * was short must never resolve to 'agree' by arithmetic coincidence.
 */
export type Reconciliation = 'agree' | 'scoped' | 'disagree' | 'unmeasured';

export function reconcile(input: {
  listed: number;
  counter: number | null;
  /** TRUE when the read covered every country — only then can the two be equal. */
  groupWide: boolean;
  /** FALSE when the catalogue read did not reach the server's own total. */
  complete: boolean;
}): Reconciliation {
  if (input.counter === null) return 'unmeasured';
  if (!input.complete) return 'disagree';
  if (input.counter === input.listed) return 'agree';
  return input.groupWide ? 'disagree' : 'scoped';
}

/** One flagged NAME row, flattened for the list. */
export interface FlaggedName {
  id: string;
  code: string;
  name: string;
  country: CountryCode | null;
}

/**
 * The flagged rows, in the order the server already returns them (country, then code) —
 * the order a reader works a list in.
 *
 * `country` is `null` when a row's `country_id` is not one this build knows: an honest
 * blank beats inventing a code, and the row must still be LISTED — it is counted by
 * `/health` either way, and dropping it is how a screen quietly under-reports.
 */
export function flaggedNames(rows: readonly ProductDto[]): FlaggedName[] {
  return rows
    .filter((row) => row.verify_before_prod)
    .map((row) => ({
      id: row.id,
      code: row.code,
      name: row.name,
      country: COUNTRY_CODE_BY_ID[row.country_id] ?? null,
    }));
}

/** One flagged PRICE version — a different table, a different meaning, its own section. */
export interface FlaggedPrice {
  key: string;
  code: string;
  country: CountryCode;
  currency: string;
  effective_from: string;
  effective_to: string | null;
}

/**
 * Flagged `country_products` versions, CLOSED ones excluded.
 *
 * A superseded price version cannot be cleared by any route, so listing it would put a
 * permanent item on a review list — the same window rule `/health` applied to this
 * counter before the counter itself was retired.
 *
 * ⚠ NO AMOUNT IS CARRIED. `price` is money and this screen's gate (`config.manage`) does
 * not imply `amounts.view`, so the row names the currency and the dates and stops there.
 */
export function flaggedPrices(
  cc: CountryCode,
  rows: readonly CountryProductDto[],
): FlaggedPrice[] {
  const today = new Date().toISOString().slice(0, 10);
  return rows
    .filter((row) => row.verify_before_prod)
    .filter((row) => row.effective_to === null || row.effective_to >= today)
    // A price the owner ruled BLANK (R3, 2026-09-22) has nothing to review — it is typed on
    // each quote by design, and listing it here would present a decision as a defect.
    .filter((row) => !row.priced_per_customer)
    .map((row) => ({
      // The row's whole identity since migration 66 — a CDF and a USD version of one product
      // may start the same day, and two list items sharing a key is a React collision.
      key: `${cc}:${row.product_id}:${row.currency}:${row.effective_from}`,
      code: row.product_code,
      country: cc,
      currency: row.currency,
      effective_from: row.effective_from,
      effective_to: row.effective_to,
    }));
}

/**
 * One country's price LIST — one currency — as the go-live checklist summarises it
 * (2026-09-22, owner: *"the go live checklist should not be empty"* and *"should not give
 * error"*). Counts only: the checklist's permission is `config.manage`, which does not imply
 * `amounts.view`, so no amount is carried.
 */
export interface PriceListSummary {
  key: string;
  country: CountryCode;
  currency: string;
  /** Products with a real price in force today. */
  priced: number;
  /** Products still at the seed's `0.00` placeholder — not priced yet. */
  placeholder: number;
  /** Products whose price is deliberately BLANK — typed on each quote (R3). Never a problem. */
  perCustomer: number;
  /** Products with versions in this list, none in force today. */
  noPriceToday: number;
  /** Products whose amount this reader may not see — counted, never guessed. */
  hidden: number;
  /**
   * The country quotes in this currency and the list holds NO row at all — nothing can be
   * quoted in it yet (Congo's USD list until finance fills it). Shown, never omitted: an
   * empty list is exactly what a salesperson meets as "no products in USD".
   */
  empty: boolean;
}

/**
 * Summarise a country's `/history` rows into one line per currency list — the checklist's
 * "Price lists" card.
 *
 * INFORMATIONAL, like every price item since G1: nothing refuses a document over any of these
 * counts, and the card says so. A per-customer price is its own column and never counted as
 * missing. A withdrawn row is left out — it is not on sale, so it is nobody's to price.
 */
export function priceListSummaries(
  cc: CountryCode,
  rows: readonly CountryProductDto[],
  /**
   * The currencies the country quotes in (`/products/currencies`), when known. Each one gets
   * a line even with no rows — found at review: grouping the rows alone made an EMPTY list
   * (Congo's USD before finance fills it) vanish from the card.
   */
  quoteCurrencies: readonly string[] = [],
): PriceListSummary[] {
  const byList = new Map<string, CountryProductDto[]>();
  for (const row of rows) {
    const key = `${row.currency}`;
    const bucket = byList.get(key);
    if (bucket === undefined) byList.set(key, [row]);
    else bucket.push(row);
  }
  const out: PriceListSummary[] = [];
  for (const [currency, listRows] of byList) {
    const byProduct = new Map<string, CountryProductDto[]>();
    for (const row of listRows) {
      const bucket = byProduct.get(row.product_id);
      if (bucket === undefined) byProduct.set(row.product_id, [row]);
      else bucket.push(row);
    }
    const summary: PriceListSummary = {
      key: `${cc}:${currency}`,
      country: cc,
      currency,
      priced: 0,
      placeholder: 0,
      perCustomer: 0,
      noPriceToday: 0,
      hidden: 0,
      empty: false,
    };
    for (const versions of byProduct.values()) {
      const current = versions.find((row) => row.window_state === 'current');
      if (current === undefined) {
        summary.noPriceToday += 1;
        continue;
      }
      if (!current.active) continue;
      if (current.priced_per_customer) summary.perCustomer += 1;
      else if (current.price === null || isMaskedAmount(current.price)) summary.hidden += 1;
      else if (/^0+(\.0+)?$/.test(current.price.trim())) summary.placeholder += 1;
      else summary.priced += 1;
    }
    out.push(summary);
  }
  for (const currency of quoteCurrencies) {
    if (byList.has(currency)) continue;
    out.push({
      key: `${cc}:${currency}`,
      country: cc,
      currency,
      priced: 0,
      placeholder: 0,
      perCustomer: 0,
      noPriceToday: 0,
      hidden: 0,
      empty: true,
    });
  }
  return out.sort((a, b) => a.currency.localeCompare(b.currency));
}

